import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  useAddTeamMember,
  useGetTeamMembers,
  useRemoveTeamMember,
  useSearchUsers,
} from '@/application/services/user.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { Button } from '@/ui/components/common/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/components/common/Dialog'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { Input } from '@/ui/components/forms/Input'

/**
 * Section for managing the investigation's team (investigators). Visible only
 * to privileged users (streamer/moderator/admin) or the investigation author.
 * Owns the add-investigator dialog state, the user-search query, and the
 * team-member add/remove mutations.
 */
export function TeamManagementSection({
  investigationId,
  authorId,
}: {
  investigationId: string
  authorId?: string | null
}) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const isPrivileged =
    user?.role === 'streamer' || user?.role === 'moderator' || user?.role === 'admin'
  const isAuthor = authorId && user?.id === authorId
  const canManageTeam = isPrivileged || isAuthor

  const { data: teamMembers, isLoading: teamLoading } = useGetTeamMembers(investigationId)
  const { mutateAsync: addTeamMember, isPending: isAdding } = useAddTeamMember(investigationId)
  const { mutateAsync: removeTeamMember } = useRemoveTeamMember(investigationId)
  const { data: searchResults } = useSearchUsers(searchQuery)

  if (!canManageTeam) return null

  const handleRemove = async (userId: string) => {
    if (window.confirm(t('admin.team_remove_confirm'))) {
      try {
        await removeTeamMember(userId)
        toast.success(t('admin.team_removed'))
      } catch {
        toast.error(t('common.error'))
      }
    }
  }

  const handleAdd = async (userId: string) => {
    try {
      await addTeamMember({ userId })
      toast.success(t('admin.team_added'))
      setAddDialogOpen(false)
      setSearchQuery('')
    } catch {
      toast.error(t('common.error'))
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl font-bold">{t('admin.team_title')}</h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAddDialogOpen(true)}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          {t('admin.team_add')}
        </Button>
      </div>

      {teamLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : !teamMembers || teamMembers.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">
          {t('admin.team_empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {teamMembers.map((member) => (
            <div
              key={member.userId}
              className="flex items-center justify-between p-3 bg-card border border-border rounded-lg"
            >
              <div>
                <p className="text-sm font-medium">
                  {member.displayName || member.username || member.userId}
                </p>
                <p className="text-xs text-muted-foreground">@{member.username}</p>
              </div>
              <button
                onClick={() => handleRemove(member.userId)}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors px-2 py-1 rounded"
              >
                {t('admin.team_remove')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Investigator Dialog */}
      <Dialog
        open={addDialogOpen}
        onOpenChange={(open) => {
          setAddDialogOpen(open)
          if (!open) setSearchQuery('')
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.team_add_dialog_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder={t('admin.team_search_placeholder')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery.length >= 2 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {!searchResults || searchResults.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('admin.team_no_results')}
                  </p>
                ) : (
                  searchResults.map((user) => (
                    <button
                      key={user.userId}
                      onClick={() => handleAdd(user.userId)}
                      disabled={isAdding}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 text-left transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium">{user.displayName || user.username}</p>
                        <p className="text-xs text-muted-foreground">@{user.username}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
